import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PrinterDTO, PrinterRegistrationForm } from '../../../types/hardware/PrinterTypes';
import { EntityStateDTO } from '../../../types/common/EntityStateDTO';
import { normalizeSaleCopies } from '../../../features/printer/printerSaleCopies';

interface PrinterState {
  form: PrinterRegistrationForm;
  list: PrinterDTO[];
  isSubmitting: boolean;
  error: string | null;
  showRegistrationModal: boolean;
  showEditModal: boolean;
}

const initialState: PrinterState = {
  form: {
    name: '',
    model: '',
    printerType: '',
    ipAddress: '',
    port: 9100,
    branchId: null,

    // ✅ Campos manuales adicionales
    paperWidthMm: 80,          // valor por defecto (puedes poner 58 o 72 según quieras)
    defaultCodePage: 'PC850',  // código de página por defecto
    supportsQr: true,
    supportsPdf417: true,
    supportsDatamatrix: true,
    supportsCutter: true,
    saleCopies: 1,
  },
  list: [],
  isSubmitting: false,
  error: null,
  showRegistrationModal: false,
  showEditModal: false,
};


const printerSlice = createSlice({
  name: 'printer',
  initialState,
  reducers: {
    setSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setPrinterList(state, action: PayloadAction<PrinterDTO[]>) {
      state.list = action.payload.map((printer) => ({
        ...printer,
        saleCopies: normalizeSaleCopies(printer.saleCopies),
      }));
    },
    setPrinterForm(state, action: PayloadAction<PrinterRegistrationForm>) {
      state.form = {
        ...action.payload,
        saleCopies: normalizeSaleCopies(action.payload.saleCopies),
      };
      state.error = null;
    },
    updatePrinterField(
      state,
      action: PayloadAction<{ field: keyof PrinterRegistrationForm; value: any }>
    ) {
      const { field, value } = action.payload;
      state.form = {
        ...state.form,
        [field]: value,
      };
    },
    resetPrinterForm(state) {
      state.form = initialState.form;
      state.isSubmitting = false;
      state.error = null;
    },
    setShowRegistrationModal(state, action: PayloadAction<boolean>) {
      state.showRegistrationModal = action.payload;
    },
    setShowEditModal(state, action: PayloadAction<boolean>) {
      state.showEditModal = action.payload;
    },
    closeRegistrationModalAndReset(state) {
      state.showRegistrationModal = false;
      state.form = initialState.form;
      state.isSubmitting = false;
      state.error = null;
    },
    closeEditModalAndReset(state) {
      state.showEditModal = false;
      state.form = initialState.form;
      state.isSubmitting = false;
      state.error = null;
    },
    updatePrinterStatus(state, action: PayloadAction<EntityStateDTO>) {
      const { id, active } = action.payload;
      const index = state.list.findIndex(p => p.id === id);
      if (index !== -1) {
        state.list[index].active = active;
      }
    },
  },
});

export const {
  setSubmitting,
  setError,
  setPrinterList,
  setPrinterForm,
  updatePrinterField,
  resetPrinterForm,
  setShowRegistrationModal,
  setShowEditModal,
  closeRegistrationModalAndReset,
  closeEditModalAndReset,
  updatePrinterStatus,
} = printerSlice.actions;

export default printerSlice.reducer;
